import type { StatusTone } from '@vnetwork/ui/data';
import type { ServiceStatus } from '@/api/services';

/**
 * Service domain formatting — status tones + human labels for the standalone
 * Services module. Mirrors the legacy `SERVICE_STATUS` colour + label intent on
 * the @ui tone palette (legacy `primary`→info, `error`→danger).
 */

/** Service status → @ui `StatusPill`/`Badge` tone. */
export function serviceStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'waiting-confirm':
    case 'waiting-create-service':
    case 'on-resubmit-information':
      return 'info';
    case 'trialing':
    case 'pending-terminated':
      return 'warning';
    case 'cancelled':
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Human labels for `service_status` (English-first, matches legacy intent). */
export const SERVICE_STATUS_LABELS: Record<ServiceStatus, string> = {
  active: 'Active',
  'waiting-confirm': 'Waiting confirmation',
  trialing: 'Trialing',
  rejected: 'Rejected',
  'on-resubmit-information': 'Resubmit info',
  cancelled: 'Cancelled',
  'waiting-create-service': 'Waiting creation',
  'pending-terminated': 'Pending terminated',
};

export function serviceStatusLabel(status?: string): string {
  if (!status) return '—';
  return SERVICE_STATUS_LABELS[status as ServiceStatus] ?? status;
}

/** Options for the status FilterBuilder select (the main list's status set). */
export const SERVICE_STATUS_OPTIONS: { value: ServiceStatus; label: string }[] = [
  { value: 'waiting-confirm', label: 'Waiting confirmation' },
  { value: 'waiting-create-service', label: 'Waiting creation' },
  { value: 'active', label: 'Active' },
  { value: 'pending-terminated', label: 'Pending terminated' },
  { value: 'cancelled', label: 'Cancelled' },
];
